/**
 * The hidden-items rules, with no I/O: the seed, the last list loaded, and the
 * filters built from it. utils/nsfw-list.ts loads and saves the list.
 *
 * Kept apart because the filter builders import these rules, and the filter
 * builders reach the browser (filter-drawer.tsx -> facet-affinity.ts ->
 * filter-builders.ts). When the rules lived beside the loader, its
 * dynamic import of the ES client pulled the client and undici into the browser
 * bundle, and every Vercel build from 03c6c6eb7 on failed on `node:crypto`.
 * Nothing here may import a server module.
 */
import type { NsfwList } from '../schemas/api/admin/nsfw-list';
// The one value import allowed: a file with no imports of its own.
import { JEV_KINDS_SEED } from './not-recommended-kinds';
import type { EsFilter } from './elasticsearch/types';

/**
 * What the list starts as, chosen from prod on 2026-10-25: the two categories,
 * and the live tags for swimwear, underwear and adult items. Left out on
 * purpose: "nylon" (mostly bags and jackets) and "underwear & socks" (socks).
 */
export const NSFW_LIST_SEED: NsfwList = {
  categoryIds: [277, 278], // Swimwear; Sleepwear & Intimates
  tags: [
    'swimwear',
    "women's one-piece swimsuits",
    "men's swimwear",
    "children's swimwear",
    'bikini sets',
    'underwear',
    'womens-bras',
    'bralette',
    'girls underwear',
    'adult wellness',
    'playboy',
  ],
  jevKinds: JEV_KINDS_SEED,
};

let cached: { list: NsfwList; at: number } | null = null;

/** The last list loaded and when, or null. For the loader's cache check. */
export function cachedNsfwList(): { list: NsfwList; at: number } | null {
  return cached;
}

/** Record a list the loader read or saved, so the builders use it at once. */
export function rememberNsfwList(list: NsfwList, at: number): void {
  cached = { list, at };
}

/** Test seam. */
export function resetNsfwListCache(): void {
  cached = null;
}

/**
 * Every tag once, lowercase, in the order given; every category id once; each
 * Jev kind once (the first wins), and the seed kinds for a list saved before
 * they could be edited.
 */
export function normalizeNsfwList(list: NsfwList): NsfwList {
  const seen = new Set<string>();
  const jevKinds = (list.jevKinds?.length ? list.jevKinds : JEV_KINDS_SEED).filter((k) => !seen.has(k.kind) && seen.add(k.kind));
  return {
    ...list,
    categoryIds: [...new Set(list.categoryIds)],
    tags: [...new Set(list.tags.map((t) => t.trim().toLowerCase()).filter(Boolean))],
    jevKinds,
  };
}

/** What Jev hides, from the last list loaded (or the seed). */
export function jevKinds(list: NsfwList = nsfwList()): NonNullable<NsfwList['jevKinds']> {
  return list.jevKinds?.length ? list.jevKinds : JEV_KINDS_SEED;
}

/** The list as last loaded, or the seed. Synchronous, for the filter builders. */
export function nsfwList(): NsfwList {
  return cached?.list ?? NSFW_LIST_SEED;
}

/**
 * Listings Jev has not judged yet (owner, 2026-10-28: "i don't want items in
 * the index if they're not evaluated"). Out of everything, typed search too,
 * unlike the hidden items below, which a typed search still finds.
 */
export const UNJUDGED_FILTER: EsFilter = { term: { nsfw_pending: true } };

/**
 * The must_not clauses that keep hidden listings out: Jev's judgment, plus
 * every listed category and tag.
 */
export function hiddenItemFilters(list: NsfwList = nsfwList()): EsFilter[] {
  const filters: EsFilter[] = [{ term: { not_recommended: true } }];
  if (list.categoryIds.length) filters.push({ terms: { categoryId: list.categoryIds } });
  if (list.tags.length) filters.push({ terms: { tags: list.tags } });
  return filters;
}

/** The same rule for a document already fetched (rows built from ids). */
export function isHiddenDoc(
  doc: { not_recommended?: boolean; categoryId?: number | null; tags?: string[] | null },
  list: NsfwList = nsfwList()
): boolean {
  if (doc.not_recommended === true) return true;
  if (doc.categoryId != null && list.categoryIds.includes(Number(doc.categoryId))) return true;
  return (doc.tags ?? []).some((t) => list.tags.includes(String(t).toLowerCase()));
}

/**
 * Whether a browse request asked for listed items: every category it names is
 * on the list, or every tag it names is. A request that names neither, or mixes
 * in anything off the list, did not.
 */
export function asksForHiddenItems(
  request: { categoryId?: number | number[]; tags?: string[] },
  list: NsfwList = nsfwList()
): boolean {
  const categories = request.categoryId === undefined ? [] : ([] as number[]).concat(request.categoryId);
  const tags = (request.tags ?? []).map((t) => t.toLowerCase());
  if (categories.length && categories.every((c) => list.categoryIds.includes(Number(c)))) return true;
  return tags.length > 0 && tags.every((t) => list.tags.includes(t));
}
